import { LoadPanel } from 'devextreme-react/load-panel';
import { TagBox } from 'devextreme-react/tag-box';
import { TextBox } from 'devextreme-react/text-box';
import { Tooltip } from 'devextreme-react/tooltip';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Draggable from 'react-draggable';
import { Col, Row } from 'reactstrap';
import logo from '../../assets/images/custom/uklogo.jpg';
import { useAuth } from '../../auth';
import api from '../../services/api-configuration-v2';
import { getBrand } from '../../services/getBrand';


// Custom Modal Component
const CustomModal = ({ isOpen, onClose, children }) => {
    const [isAnimating, setIsAnimating] = useState(false);
    const [shouldRender, setShouldRender] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [isLoading, setIsLoading] = useState(false);


    useEffect(() => {
        if (document.querySelector(".page-main-header")) document.querySelector(".page-main-header").classList.add('open');
        if (document.querySelector(".page-sidebar")) document.querySelector(".page-sidebar").classList.add('open');

        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (isOpen) {
            setShouldRender(true);
            document.body.style.overflow = 'hidden';
            requestAnimationFrame(() => setIsAnimating(true));
        } else {
            setIsAnimating(false);
            document.body.style.overflow = '';
            const timer = setTimeout(() => setShouldRender(false), 300);
            return () => clearTimeout(timer);
        }
    }, [isOpen]);

    if (!shouldRender) return null;

    // Check if we're in fullscreen mode
    const isFullscreen = !!document.fullscreenElement;
    const targetElement = isFullscreen ? document.fullscreenElement : document.body;

    return createPortal(
        <div className={`modal-wrapper ${isAnimating ? 'modal-visible' : ''} ${isMobile ? 'mobile' : ''}`}>
            <div className="modal-backdrop" onClick={onClose} />
            <div className="modal-box">
                {children}
            </div>
            <style jsx global>{`
                .modal-wrapper {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 2147483647;
                    opacity: 0;
                    visibility: hidden;
                    transition: all 0.3s ease-out;
                    will-change: opacity, visibility;
                }

                .modal-visible {
                    opacity: 1 !important;
                    visibility: visible !important;
                }

                .modal-backdrop {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background-color: rgba(0, 0, 0, 0.5);
                    z-index: -1;
                    opacity: 0;
                    transition: opacity 0.3s ease-out;
                    will-change: opacity;
                }

                .modal-visible .modal-backdrop {
                    opacity: 1;
                }

                .modal-box {
                    background: white;
                    border-radius: 12px;
                    width: 90%;
                    max-width: 800px;
                    max-height: 90vh;
                    position: relative;
                    box-shadow: 0 5px 20px rgba(0, 0, 0, 0.15);
                    display: flex;
                    flex-direction: column;
                    transform: translateY(20px);
                    opacity: 0;
                    transition: all 0.3s ease-out;
                    will-change: transform, opacity;
                    overflow: hidden;
                }

                .modal-visible .modal-box {
                    transform: translateY(0);
                    opacity: 1;
                }

                /* Mobile Styles */
                @media (max-width: 768px) {
                    .modal-wrapper.mobile {
                        align-items: flex-end;
                    }

                    .modal-wrapper.mobile .modal-box {
                        width: 100%;
                        max-width: 100%;
                        margin: 0;
                        border-radius: 20px 20px 0 0;
                        max-height: 95vh;
                        transform: translateY(100%);
                    }

                    .modal-wrapper.mobile.modal-visible .modal-box {
                        transform: translateY(0);
                    }

                    .modal-wrapper.mobile .chassis-modal-header {
                        border-radius: 20px 20px 0 0;
                        padding: 16px;
                    }

                    .modal-wrapper.mobile .modal-body {
                        padding-bottom: env(safe-area-inset-bottom);
                    }
                }

                /* iOS Safari specific styles */
                @supports (-webkit-touch-callout: none) {
                    .modal-wrapper.mobile .modal-box {
                        padding-bottom: env(safe-area-inset-bottom);
                    }
                }
            `}</style>
        </div>,
        targetElement
    );
};

/**
 * Depot Stock Status Component
 */
const YoneticiEkranlari = () => {
    // Separate state for chassis search and depot filtering
    const [chassisSearchQuery, setChassisSearchQuery] = useState('');
    const [depotData, setDepotData] = useState([]);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const containerRef = useRef(null);
    const intervalRef = useRef(null);
    const { hasRole } = useAuth(23)

    // Brand filter state
    const [selectedBrands, setSelectedBrands] = useState([]);
    const [availableBrands, setAvailableBrands] = useState([]);
    const [userBrandIds, setUserBrandIds] = useState([]);
    const [hasError, setHasError] = useState(false);

    // Chassis search modal state
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [chassisInfo, setChassisInfo] = useState(null);
    const [searchError, setSearchError] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const [showScrollTop, setShowScrollTop] = useState(false);

    // Drag tracking for scroll to top button
    const [isDragging, setIsDragging] = useState(false);
    const [dragStartTime, setDragStartTime] = useState(0);
    const timeoutIdRef = useRef(null);

    // Hata durumu için state
    const [loadError, setLoadError] = useState(null);

    // Function to get user's authorized brands
    const getUserAuthorizedBrands = async () => {
        try {
            const employeeId = parseInt(Buffer.from(localStorage.getItem("employee_id"), 'base64').toString('ascii'));
            const response = await api.post('/employee/getByEmployeeID', { employee_id: employeeId });

            if (response.data && response.data.brand_ids) {
                setUserBrandIds(response.data.brand_ids);
                return response.data.brand_ids;
            }
            throw new Error('Kullanıcı bilgileri alınamadı.');
        } catch (error) {
            console.error('Kullanıcı marka yetkileri alınırken hata:', error);
            throw new Error('Kullanıcı bilgileri alınırken hata meydana geldi.');
        }
    };

    // Function to fetch and filter brands
    const fetchAndFilterBrands = async () => {
        try {
            const allBrands = await getBrand();
            const authorizedBrandIds = await getUserAuthorizedBrands();

            // Kullanıcının yetkisi olan markaları filtrele
            const filteredBrands = allBrands.filter(brand =>
                authorizedBrandIds.includes(brand.id)
            );

            setAvailableBrands(filteredBrands);

            // Varsayılan olarak tüm yetkili markaları seç
            if (filteredBrands.length > 0) {
                const defaultSelectedBrands = filteredBrands.map(brand => brand.id);
                setSelectedBrands(defaultSelectedBrands);
            } else {
                setLoadError('Kullanıcının yetkili olduğu marka bulunamadı. Lütfen yetkili iletişime geçin.');
                setIsLoading(false);
            }
        } catch (error) {
            console.error('Markalar alınırken hata:', error);
            setAvailableBrands([]);
            setLoadError(error.message || 'Markalar yüklenirken bir hata oluştu.');
            setIsLoading(false);
        }
    };

    // Function to toggle fullscreen
    const toggleFullscreen = () => {
        if (!document.fullscreenElement) {
            containerRef.current.requestFullscreen().then(() => {
                setIsFullscreen(true);
                // Hide header and sidebar when entering fullscreen
                if (document.querySelector(".page-main-header")) document.querySelector(".page-main-header").classList.add('d-none');
                if (document.querySelector(".page-sidebar")) document.querySelector(".page-sidebar").classList.add('d-none');
            }).catch(err => {
                console.error(`Error attempting to enable fullscreen: ${err.message}`);
            });
        } else {
            document.exitFullscreen().then(() => {
                setIsFullscreen(false);
                // Show header and sidebar when exiting fullscreen
                if (document.querySelector(".page-main-header")) document.querySelector(".page-main-header").classList.remove('d-none');
                if (document.querySelector(".page-sidebar")) document.querySelector(".page-sidebar").classList.remove('d-none');
            }).catch(err => {
                console.error(`Error attempting to exit fullscreen: ${err.message}`);
            });
        }
    };

    // Listen for fullscreen change events
    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullscreen(!!document.fullscreenElement);
            if (!document.fullscreenElement) {
                // Show header and sidebar when exiting fullscreen
                if (document.querySelector(".page-main-header")) document.querySelector(".page-main-header").classList.remove('d-none');
                if (document.querySelector(".page-sidebar")) document.querySelector(".page-sidebar").classList.remove('d-none');
            }
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
    }, []);

    // Function to fetch fresh data from API
    const fetchData = async () => {
        // Eğer henüz brand'lar yüklenmemişse veya seçili brand yoksa istek atma
        if (availableBrands.length === 0 || selectedBrands.length === 0) {
            return;
        }

        try {
            const response = await api.post('/getLiveOperationTrackingForManagementPanel', {
                brand_ids: selectedBrands,
            });
            setDepotData(response.data);
            setIsLoading(false);
            setHasError(false); // Başarılı istekte error'ı temizle
        } catch (error) {
            console.error('Veri çekme hatası:', error);
            setIsLoading(false);
            setHasError(true); // Error durumunda hasError'ı true yap
        }
    };

    // Auto-refresh data every 5 seconds - sadece brand'lar yüklendikten sonra
    useEffect(() => {
        let isComponentMounted = true;

        const fetchDataWithCheck = async () => {
            // Brand'lar henüz yüklenmemişse veya seçili brand yoksa bekle
            if (!isComponentMounted || availableBrands.length === 0 || selectedBrands.length === 0) {
                return;
            }

            try {
                const response = await api.post('/getLiveOperationTrackingForManagementPanel', {
                    brand_ids: selectedBrands,
                });

                if (!isComponentMounted) return;

                setDepotData(response.data);
                setIsLoading(false);
                setHasError(false); // Başarılı istekte error'ı temizle
            } catch (error) {
                console.error('Veri çekme hatası:', error);
                if (isComponentMounted) {
                    setIsLoading(false);
                    setHasError(true); // Error durumunda hasError'ı true yap
                }
            }

            // 5 saniye sonra yeni istek at
            if (isComponentMounted) {
                timeoutIdRef.current = setTimeout(fetchDataWithCheck, 5000);
            }
        };

        // Sadece brand'lar yüklendikten ve seçili brand'lar olduğunda başlat
        if (availableBrands.length > 0 && selectedBrands.length > 0) {
            fetchDataWithCheck();
        }

        return () => {
            isComponentMounted = false;
            if (timeoutIdRef.current) {
                clearTimeout(timeoutIdRef.current);
            }
        };
    }, [availableBrands, selectedBrands]); // selectedBrands dependency'si eklendi

    // Load user's authorized brands on component mount
    useEffect(() => {
        fetchAndFilterBrands();
    }, []);

    // Brand seçimi değiştiğinde veriyi yenile
    useEffect(() => {
        if (availableBrands.length > 0 && selectedBrands.length > 0) {
            fetchData();
        }
    }, [selectedBrands, availableBrands]);

    // Handle chassis search input change
    const handleChassisSearchChange = (e) => {
        setChassisSearchQuery(e.value);
        setSearchError(false); // Reset error on new input
    };

    // Handle chassis search
    const handleChassisSearch = async () => {
        if (!chassisSearchQuery.trim()) return;

        setIsSearching(true); // Start loading
        setChassisInfo(null); // Reset previous data
        setSearchError(false);

        try {
            const response = await api.post('/getVehicleByEmployeeID', {
                chassis_no: chassisSearchQuery.trim().toUpperCase(),
                employee_id: parseInt(Buffer.from(localStorage.getItem("employee_id"), 'base64').toString('ascii'))
            });

            const data = response.data;
            console.log('Şase arama sonucu:', data);
            if (data) {

                const formattedData = {
                    sase: data.chassis_no,
                    marka: data.brand || "-",
                    model: data.model || "-",
                    blokajDurumu: (data.blocking || data.blocking_shipping) ? "Var" : "Yok",
                    millilesmeDurumu: data.domestic_area === "ithal" ? "Yok" : "Gümrüklendi",
                    pdi: data.pdi_status ? "OK" : "Hayır",
                    sonBulunduguDepo: data.located_warehouse || "-",
                    depodaMi: data.is_active ? "Evet" : "Hayır",
                    sevkDurumu: data.vehicle_status || "-",
                    sevkTarihi: data.shipment_date ? moment(data.shipment_date).format('DD-MM-YYYY') : "-",
                    faturaDurumu: data.invoice_data && data.invoice_data?.length > 0 ? "Var" : "Yok",
                    faturaBayisi: data.invoice_data && data.invoice_data?.length > 0 ? data.invoice_data[0].fatura_bayi : "-",
                    faturaTarihi: data.invoice_data && data.invoice_data?.length > 0 ? moment(data.invoice_data[0].fatura_tarihi).format('DD-MM-YYYY') : "-"
                };

                setChassisInfo(formattedData);
                setSearchError(false);
                setIsModalOpen(true);

            } else {
                setChassisInfo(null);
                setSearchError('bulunamadi');
            }
        } catch (error) {
            console.error('Şase arama hatası:', error);
            if (error?.message?.includes('vehicle not found')) setSearchError('bulunamadi');
            else if (error?.message?.includes('no rows in result set')) setSearchError('yetkisiz');
            else setSearchError('hata');
            setChassisInfo(null);

        } finally {
            setIsSearching(false); // End loading
        }
    };

    // Handle search key press (search on Enter key)
    const handleSearchKeyPress = (e) => {
        if (e.event && e.event.key === 'Enter') {
            handleChassisSearch();
        }
    };

    // PDF indirme sırasında kullanılacak
    const startDataRefresh = () => {
        fetchData();
    };

    const stopDataRefresh = () => {
        // PDF indirme sırasında istekleri durdur
        if (timeoutIdRef.current) {
            clearTimeout(timeoutIdRef.current);
            timeoutIdRef.current = null;
        }
    };

    // Handle PDF download
    const handlePdfDownload = async () => {
        const pdfButton = document.getElementById('pdf-download-button');
        const loadPanel = document.getElementById('pdf-load-panel');

        if (pdfButton.disabled || !containerRef.current) return;

        // Show loading state immediately
        pdfButton.disabled = true;
        pdfButton.innerHTML = `
            <i class="fa fa-spinner fa-spin"></i>
            <span class="button-text">İndiriliyor...</span>
        `;
        loadPanel.style.display = 'block';
        stopDataRefresh(); // PDF indirme başladığında istekleri durdur

        // Use setTimeout to ensure loading state is visible immediately
        setTimeout(async () => {
            try {
                // Create PDF container with full-screen styling
                const pdfContainer = document.createElement('div');
                pdfContainer.style.position = 'absolute';
                pdfContainer.style.left = '-9999px';
                pdfContainer.style.width = '1600px';
                pdfContainer.style.backgroundColor = '#FFFFFF';
                pdfContainer.style.padding = '20px 40px';
                document.body.appendChild(pdfContainer);

                // Create PDF content wrapper
                const pdfContent = document.createElement('div');
                pdfContent.className = 'pdf-content';
                pdfContainer.appendChild(pdfContent);

                // Create PDF header
                const header = document.createElement('div');
                header.className = 'pdf-header';

                // Create logo image element separately
                const logoImg = document.createElement('img');
                logoImg.src = logo;
                logoImg.alt = 'UK Lojistik';
                logoImg.style.width = '60px';
                logoImg.style.height = '60px';

                // Get selected brand names
                const selectedBrandNames = selectedBrands.map(brandCode =>
                    availableBrands.find(brand => brand.id === brandCode)?.brand_name || brandCode
                ).join(', ');

                header.innerHTML = `
                    <div class="pdf-header-content">
                        <div class="pdf-logo-section">
                            <div class="pdf-logo"></div>
                            <div class="pdf-company-info">
                                <div>info@uklojistik.com</div>
                                <div>www.uklojistik.com</div>
                                <div>+90 (216) 688 08 98</div>
                            </div>
                        </div>
                        <div class="pdf-date-section">
                            <div class="pdf-date-label">Rapor Tarihi</div>
                            <div class="pdf-date-value">${moment().format('DD.MM.YYYY HH:mm:ss')}</div>
                        </div>
                    </div>
                    <div class="pdf-title">DEPO STOK DURUMU RAPORU</div>
                    <div class="pdf-brands-section">
                        <div class="pdf-brands-label">Seçili Markalar:</div>
                        <div class="pdf-brands-list">${selectedBrandNames}</div>
                    </div>
                `;

                // Add logo image to the container after innerHTML
                const logoContainer = header.querySelector('.pdf-logo');
                logoContainer.appendChild(logoImg);

                pdfContent.appendChild(header);

                // Clone and modify depot cards for PDF
                const depotsContainer = document.createElement('div');
                depotsContainer.className = 'pdf-depots-container';

                depotData.forEach(depot => {
                    // En büyük 4 bölgeyi al
                    const topRegions = [...depot.summary]
                        .sort((a, b) => b.value - a.value)
                        .slice(0, 4);

                    const depotCard = document.createElement('div');
                    depotCard.className = 'pdf-depot-card';
                    depotCard.innerHTML = `
                        <div class="pdf-depot-header">
                            <h2>${depot.name}</h2>
                        </div>
                        <div class="pdf-depot-body">
                            <div class="pdf-left-stats">
                                <div class="pdf-stat-row">
                                    <div class="pdf-stat-label">Faturasız</div>
                                    <div class="pdf-stat-value">${depot.stats.faturasiz}</div>
                                </div>
                                <div class="pdf-stat-row">
                                    <div class="pdf-stat-label">Blokajlı</div>
                                    <div class="pdf-stat-value">${depot.stats.blokajli}</div>
                                </div>
                                <div class="pdf-stat-row">
                                    <div class="pdf-stat-label">Hazırlanıyor</div>
                                    <div class="pdf-stat-value">${depot.stats.hazirlaniyor}</div>
                                </div>
                                <div class="pdf-stat-row">
                                    <div class="pdf-stat-label">Bayi Sevk </div>
                                    <div class="pdf-stat-value">${depot.dealer_dispatch.toplam}</div>
                                </div>
                            </div>
                            <div class="pdf-total-section">
                                <div class="pdf-total-circle">
                                    <div class="pdf-total-label">Toplam</div>
                                    <div class="pdf-total-value">${depot.toplam}</div>
                                </div>
                            </div>
                            <div class="pdf-right-stats">
                                <div class="pdf-stats-grid">
                                    ${topRegions.map((region, index) => `
                                        <div class="pdf-stat-box">
                                            <div class="pdf-stat-box-label">${region.name}</div>
                                            <div class="pdf-stat-box-value">${Math.max(0, region.value)}</div>
                                        </div>
                                    `).join('')}
                                </div>
                            </div>
                        </div>
                    `;
                    depotsContainer.appendChild(depotCard);
                });

                pdfContent.appendChild(depotsContainer);

                // Add PDF-specific styles
                const style = document.createElement('style');
                style.textContent = `
                    .pdf-content {
                        font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                        color: #212529;
                        width: 100%;
                    }

                    .pdf-header-content {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        margin-bottom: 10px;
                        padding-bottom: 8px;
                        border-bottom: 1px solid #dee2e6;
                    }

                    .pdf-logo-section {
                        display: flex;
                        align-items: center;
                        gap: 15px;
                    }

                    .pdf-logo {
                        width: 60px;
                        height: 60px;
                        border-radius: 6px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        overflow: hidden;
                    }

                    .pdf-logo img {
                        width: 50px;
                        height: 50px;
                        object-fit: contain;
                    }

                    .pdf-company-info {
                        color: #6c757d;
                        font-size: 12px;
                        line-height: 1.3;
                    }

                    .pdf-date-section {
                        text-align: right;
                    }

                    .pdf-date-label {
                        font-weight: 600;
                        margin-bottom: 2px;
                        font-size: 12px;
                        color: #6c757d;
                    }

                    .pdf-date-value {
                        font-size: 14px;
                        font-weight: 600;
                        color: #212529;
                    }

                    .pdf-title {
                        text-align: center;
                        font-weight: bold;
                        font-size: 22px;
                        margin: 8px 0 20px;
                        color: #2B53C6;
                    }

                    .pdf-brands-section {
                        background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
                        border: 1px solid #dee2e6;
                        border-radius: 8px;
                        padding: 12px 16px;
                        margin: 15px 0 25px;
                        display: flex;
                        align-items: center;
                        gap: 12px;
                        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
                    }

                    .pdf-brands-label {
                        font-weight: 600;
                        color: #495057;
                        font-size: 14px;
                        white-space: nowrap;
                    }

                    .pdf-brands-list {
                        font-weight: 500;
                        color: #2B53C6;
                        font-size: 14px;
                        flex: 1;
                    }

                    .pdf-depots-container {
                        width: 100%;
                    }

                    .pdf-depot-card {
                        background: white;
                        border: 2px solid #dee2e6;
                        border-radius: 6px;
                        margin-bottom: 40px;
                        page-break-inside: avoid;
                        overflow: hidden;
                        width: 100%;
                    }

                    .pdf-depot-header {
                        padding: 15px 20px;
                        border-bottom: 1px solid #dee2e6;
                    }

                    .pdf-depot-header h2 {
                        margin: 0;
                        font-size: 24px;
                        color: #2B53C6;
                        font-weight: 500;
                    }

                    .pdf-depot-body {
                        padding: 20px;
                        display: grid;
                        grid-template-columns: 2fr 1fr 2fr;
                        grid-template-areas: "left-stats total-section right-stats";
                        gap: 20px;
                        align-items: start;
                    }

                    .pdf-left-stats {
                        grid-area: left-stats;
                        min-width: 0;
                    }

                    .pdf-stat-row {
                        display: flex;
                        justify-content: space-between;
                        padding: 8px 0;
                        border-bottom: 1px solid #f0f0f0;
                    }

                    .pdf-stat-label {
                        font-size: 16px;
                        color: rgb(30, 32, 34);
                        font-weight: 400;
                    }

                    .pdf-stat-value {
                        font-weight: 600;
                        font-size: 18px;
                        color: #212529;
                    }

                    .pdf-total-section {
                        grid-area: total-section;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        padding: 10px;
                        min-width: 200px;
                        margin: auto;
                    }

                    .pdf-total-circle {
                        background: linear-gradient(135deg, #2B53C6 0%, #1e40af 100%);
                        color: white;
                        width: 160px;
                        height: 160px;
                        border-radius: 50%;
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        justify-content: center;
                        box-shadow: 0 4px 12px rgba(43, 83, 198, 0.3);
                        margin: auto;
                    }

                    .pdf-total-label {
                        font-size: 19px;
                        opacity: 0.9;
                        margin-bottom: 4px;
                    }

                    .pdf-total-value {
                        font-size: 35px;
                        font-weight: 500;
                        line-height: 1;
                    }

                    .pdf-right-stats {
                        grid-area: right-stats;
                        display: flex;
                        justify-content: flex-end;
                        min-width: 0;
                    }

                    .pdf-stats-grid {
                        display: grid;
                        grid-template-columns: repeat(2, minmax(140px, 1fr));
                        gap: 15px;
                        width: 100%;
                        max-width: 400px;
                    }

                    .pdf-stat-box {
                        background-color: #e9ecef;
                        border-radius: 6px;
                        padding: 15px;
                        text-align: center;
                        min-height: 100px;
                        display: flex;
                        flex-direction: column;
                        justify-content: center;
                        align-items: center;
                    }

                    .pdf-stat-box-label {
                        font-size: 17px;
                        color: #495057;
                        margin-bottom: 5px;
                    }

                    .pdf-stat-box-value {
                        font-size: 24px;
                        font-weight: 700;
                        color: #212529;
                    }
                `;
                pdfContainer.appendChild(style);

                // Generate PDF
                const canvas = await html2canvas(pdfContainer, {
                    scale: 2,
                    useCORS: true,
                    allowTaint: true,
                    backgroundColor: '#FFFFFF',
                    logging: false,
                    width: 1600,
                    height: pdfContainer.scrollHeight
                });

                const imgData = canvas.toDataURL('image/jpeg', 1.0);
                const pdf = new jsPDF({
                    orientation: 'landscape',
                    unit: 'mm',
                    format: 'a4',
                    compress: true
                });

                const pdfWidth = pdf.internal.pageSize.getWidth();
                const pdfHeight = pdf.internal.pageSize.getHeight();
                const imgWidth = canvas.width;
                const imgHeight = canvas.height;
                const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);

                const imgX = (pdfWidth - imgWidth * ratio) / 2;
                const imgY = (pdfHeight - imgHeight * ratio) / 2;

                pdf.addImage(imgData, 'JPEG', imgX, imgY, imgWidth * ratio, imgHeight * ratio);
                pdf.save(`UK-Lojistik-Depo-Stok-Durumu-${moment().format('YYYYMMDD-HHmm')}.pdf`);

                // Cleanup
                document.body.removeChild(pdfContainer);
            } catch (error) {
                console.error('PDF oluşturma hatası:', error);
                alert('PDF oluşturulurken bir hata oluştu. Lütfen tekrar deneyin.');
            } finally {
                // Reset loading state
                const pdfButton = document.getElementById('pdf-download-button');
                const loadPanel = document.getElementById('pdf-load-panel');

                pdfButton.disabled = false;
                pdfButton.innerHTML = `
                    <i class="fa fa-file-pdf-o"></i>
                    <span class="button-text">PDF İndir</span>
                `;
                loadPanel.style.display = 'none';
                startDataRefresh();
            }
        }, 0);
    };

    // Toggle modal
    const toggleModal = () => {
        setIsModalOpen(!isModalOpen);
    };

    // Function to scroll to top
    const scrollToTop = () => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    };

    // Handle drag start
    const handleDragStart = () => {
        setDragStartTime(Date.now());
        setIsDragging(false);
    };

    // Handle drag
    const handleDrag = () => {
        setIsDragging(true);
    };

    // Handle drag stop
    const handleDragStop = () => {
        const dragDuration = Date.now() - dragStartTime;
        // If drag duration is very short and no actual dragging occurred, treat as click
        if (!isDragging && dragDuration < 200) {
            scrollToTop();
        }
        setIsDragging(false);
    };

    // Handle scroll to show/hide scroll to top button
    useEffect(() => {
        const handleScroll = () => {
            setShowScrollTop(window.pageYOffset > 150);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const animationConfig = {
        show: {
            type: 'fade',
            from: { opacity: 0 },
            to: { opacity: 1 }
        },
        hide: {
            type: 'fade',
            from: { opacity: 1 },
            to: { opacity: 0 }
        }
    };

    // Refresh function
    const handleRefresh = () => {
        // Sayfayı yenile
        window.location.reload();
    };

    return (
        <>
            <div className="page-container p-4" ref={containerRef} style={{
                maxWidth: "1800px",
                margin: "auto",
                backgroundColor: "#F6F7FB",
                position: "relative",
                zIndex: "1"
            }}>
                <div className={`live-circle-indicator ${hasError ? 'error' : availableBrands.length > 0 && selectedBrands.length === 0 ? 'waiting' : 'active'}`} id="liveIndicator">
                    <div className="live-dot"></div>
                </div>

                <Tooltip
                    target="#liveIndicator"
                    showEvent="mouseenter"
                    hideEvent="mouseleave"
                    position="top"
                    animation={animationConfig}
                    hideOnOutsideClick={false}
                >
                    <div style={{
                        padding: '0px', fontSize: '13px'
                    }}>
                        {hasError ? 'Bağlantı Hatası' : availableBrands.length > 0 && selectedBrands.length === 0 ? 'Marka seçmeyi bekliyor' : 'Sistem Aktif'}
                    </div>
                </Tooltip>

                <Row className="mx-0 mb-4 header-section w-100">
                    <Col xs={12} className="m-0 p-0">
                        <div className="header-content">
                            <div className="header-top-row">
                                {isFullscreen && <div className="header-logo-container">
                                    <img src={logo} alt="UK Lojistik" className="header-logo" />
                                </div>}
                                <div className="action-buttons">
                                    <button
                                        className="action-button fullscreen-button"
                                        onClick={toggleFullscreen}
                                        title={isFullscreen ? "Küçült" : "Tam Ekran"}
                                    >
                                        <i className={`fa ${isFullscreen ? 'fa-compress' : 'fa-expand'}`}></i>
                                    </button>
                                    <button
                                        id="pdf-download-button"
                                        className="action-button pdf-button"
                                        onClick={handlePdfDownload}
                                        disabled={depotData.length === 0 || loadError || selectedBrands.length === 0}
                                    >
                                        <i className="fa fa-file-pdf-o"></i>
                                        <span className="button-text">PDF İndir</span>
                                    </button>
                                </div>
                            </div>
                            <div className="header-left-group">
                                <div className="brand-filter-container">
                                    <TagBox
                                        id='brand-filter'
                                        dataSource={availableBrands}
                                        valueExpr="id"
                                        displayExpr="brand_name"
                                        style={{ width: "100%" }}
                                        selectAllMode="allPages"
                                        showSelectionControls={true}
                                        selectAllText="Tümü"
                                        height={40}
                                        showDataBeforeSearch={true}
                                        searchEnabled={true}
                                        searchMode="contains"
                                        searchTimeout={300}


                                        maxDisplayedTags={2}
                                        value={selectedBrands}
                                        onValueChanged={(e) => setSelectedBrands(e.value)}
                                        applyValueMode='instantly'
                                        labelMode='floating'
                                        label='Marka'
                                        disabled={availableBrands.length === 0}
                                        placeholder={availableBrands.length === 0 ? "Markalar yükleniyor..." : "Marka seçiniz"}

                                    />
                                    {availableBrands.length > 0 && selectedBrands.length === 0 && (
                                        <div className="brand-warning-container">
                                            <div className="brand-warning"
                                                style={{
                                                    fontSize: '14px',
                                                    fontWeight: '500',
                                                    color: '#dc2626',
                                                    padding: '12px 16px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '8px',
                                                    letterSpacing: '0.2px',
                                                    lineHeight: '1.4',
                                                    marginTop: '3px',
                                                    animation: 'slideDown 0.3s ease-out'
                                                }}
                                            >
                                                <i className="fa fa-exclamation-triangle" style={{ fontSize: '16px', color: '#dc2626' }}></i>
                                                <span>Lütfen en az bir marka seçiniz</span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="search-box-container" style={{
                                visibility: hasRole("canli_takip_yonetici_write") ? 'visible' : 'hidden'
                            }}>
                                <div className="search-box">
                                    <div className="search-input-wrapper">
                                        <TextBox
                                            value={chassisSearchQuery}
                                            onValueChanged={handleChassisSearchChange}
                                            onKeyUp={handleSearchKeyPress}
                                            width="100%"
                                            height={44}
                                            showClearButton={true}
                                            valueChangeEvent="keyup"
                                            label='Şase Numarası'
                                            labelMode='floating'
                                            placeholder="Örn: VR3FPHNSTPY637470"
                                            buttons={[
                                                {
                                                    name: 'search',
                                                    location: 'after',
                                                    options: {
                                                        icon: isSearching ? 'refresh' : 'search',
                                                        type: 'default',
                                                        onClick: handleChassisSearch,
                                                        stylingMode: 'contained',
                                                        disabled: isSearching || !chassisSearchQuery.trim(),
                                                        hint: isSearching ? 'Aranıyor...' : 'Şase Ara',
                                                        elementAttr: {
                                                            class: isSearching ? 'searching' : ''
                                                        }
                                                    }
                                                }
                                            ]}
                                        />
                                        {isSearching && (
                                            <div className="search-loading-indicator">
                                                <i className="fa fa-spinner fa-spin"></i>
                                            </div>
                                        )}
                                    </div>
                                    {searchError && (
                                        <div className="search-error-container">
                                            <div className="search-error"
                                                style={{
                                                    fontSize: '14px',
                                                    fontWeight: '500',
                                                    color: '#dc2626',
                                                    padding: '12px 16px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '8px',
                                                    letterSpacing: '0.2px',
                                                    lineHeight: '1.4',
                                                    marginTop: '3px',
                                                    animation: 'slideDown 0.3s ease-out'
                                                }}
                                            >
                                                <i className="fa fa-exclamation-triangle" style={{ fontSize: '16px', color: '#dc2626' }}></i>
                                                <span>
                                                    {searchError === 'yetkisiz' ? 'Bu şaseyi görüntüleme yetkiniz bulunmamaktadır' : searchError === "bulunamadi" ? 'Şase bulunamadı' : 'Bir hata oluştu.'}
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </Col>
                </Row>

                {/* Only show LoadPanel during PDF download */}
                <LoadPanel
                    id="pdf-load-panel"
                    position={{ of: '.page-container' }}
                    visible={false}
                    showIndicator={true}
                    shading={true}
                    showPane={true}
                    style={{ display: 'none' }}
                />

                {/* Custom Chassis Search Modal */}
                <CustomModal isOpen={isModalOpen} >
                    <div className="chassis-modal-header">
                        <div className="uk-header">
                            <div className="uk-title">UK Lojistik</div>
                            <div className="uk-email">destek@uklojistik.com</div>
                        </div>
                        <button className="close-btn" onClick={toggleModal}>×</button>
                    </div>
                    <div className="modal-body">
                        {chassisInfo && (
                            <div className="chassis-info-container">
                                <div className="info-row m-0">
                                    <div className="info-label m-0">Şase</div>
                                    <div className="info-value sase-value"> &nbsp;{chassisInfo.sase}</div>
                                </div>

                                <div className="info-row">
                                    <div className="info-col">
                                        <div className="info-label">Marka</div>
                                        <div className="info-value">{chassisInfo.marka}</div>
                                    </div>
                                    <div className="info-col">
                                        <div className="info-label">Model</div>
                                        <div className="info-value">{chassisInfo.model}</div>
                                    </div>
                                </div>

                                <div className="info-row">
                                    <div className="info-col">
                                        <div className="info-label">Blokaj Durumu</div>
                                        <div className="info-value">{chassisInfo.blokajDurumu}</div>
                                    </div>
                                    <div className="info-col">
                                        <div className="info-label">Millileşme Durumu</div>
                                        <div className="info-value">{chassisInfo.millilesmeDurumu}</div>
                                    </div>
                                    <div className="info-col">
                                        <div className="info-label">PDI</div>
                                        <div className="info-value">{chassisInfo.pdi}</div>
                                    </div>
                                </div>

                                <div className="info-row">
                                    <div className="info-col">
                                        <div className="info-label">Son Bulunduğu Depo</div>
                                        <div className="info-value">{chassisInfo.sonBulunduguDepo}</div>
                                    </div>
                                    <div className="info-col">
                                        <div className="info-label">Depoda mı?</div>
                                        <div className="info-value">{chassisInfo.depodaMi}</div>
                                    </div>
                                </div>

                                <div className="info-row">
                                    <div className="info-col">
                                        <div className="info-label">Sevk Durumu</div>
                                        <div className="info-value">{chassisInfo.sevkDurumu}</div>
                                    </div>
                                    <div className="info-col">
                                        <div className="info-label">Sevk Tarihi</div>
                                        <div className="info-value">{chassisInfo.sevkTarihi}</div>
                                    </div>
                                </div>

                                <div className="info-row">
                                    <div className="info-col">
                                        <div className="info-label">Fatura Durumu</div>
                                        <div className="info-value">{chassisInfo.faturaDurumu}</div>
                                    </div>
                                    <div className="info-col">
                                        <div className="info-label">Fatura Bayisi</div>
                                        <div className="info-value">{chassisInfo.faturaBayisi}</div>
                                    </div>
                                    <div className="info-col">
                                        <div className="info-label">Fatura Tarihi</div>
                                        <div className="info-value">{chassisInfo.faturaTarihi}</div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </CustomModal>

                {/* Depots list - Always show all depots, not filtered */}
                {isLoading ? (
                    <div className="loading-container">
                        <div className="loading-indicator">
                            <div className="loading-spinner"></div>
                            <div className="loading-text">Yükleniyor...</div>
                        </div>
                    </div>
                ) : (
                    <div className="depots-container fade-in">
                        {loadError && (
                            <div className="load-error-container">
                                <div className="load-error"
                                    style={{
                                        fontSize: '14px',
                                        fontWeight: '500',
                                        color: '#dc2626',
                                        padding: '12px 16px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: '8px',
                                        letterSpacing: '0.2px',
                                        lineHeight: '1.4',
                                        marginBottom: '16px',
                                        animation: 'slideDown 0.3s ease-out',
                                        background: '#fef2f2',
                                        border: '1px solid #fecaca',
                                        borderRadius: '8px'
                                    }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <i className="fa fa-exclamation-triangle" style={{ fontSize: '16px', color: '#dc2626' }}></i>
                                        <span>{loadError}</span>
                                    </div>
                                    <button
                                        onClick={handleRefresh}
                                        style={{
                                            background: '#dc2626',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '6px',
                                            padding: '6px 12px',
                                            fontSize: '12px',
                                            fontWeight: '500',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '4px',
                                            transition: 'all 0.2s ease'
                                        }}
                                        onMouseOver={(e) => e.target.style.background = '#b91c1c'}
                                        onMouseOut={(e) => e.target.style.background = '#dc2626'}
                                    >
                                        <i className="fa fa-refresh" style={{ fontSize: '12px' }}></i>
                                        Yenile
                                    </button>
                                </div>
                            </div>
                        )}
                        {depotData.map(depot => {
                            // En büyük 4 bölgeyi al
                            const topRegions = [...depot.summary]
                                .sort((a, b) => b.value - a.value)
                                .slice(0, 4);

                            return (
                                <div key={depot.id} className="depot-card mb-4 fade-in-item" style={{ maxWidth: "1500px", margin: "auto" }}>
                                    <div className="depot-header">
                                        <h2>{depot.name}</h2>
                                    </div>

                                    <div className="depot-body">
                                        <div className="left-stats">
                                            <div className="stat-row">
                                                <div className="stat-label">Faturasız</div>
                                                <div className="stat-value">{depot.stats.faturasiz}</div>
                                            </div>

                                            <div className="stat-row">
                                                <div className="stat-label">Blokajlı</div>
                                                <div className="stat-value">{depot.stats.blokajli}</div>
                                            </div>

                                            <div className="stat-row">
                                                <div className="stat-label">Hazırlanıyor</div>
                                                <div className="stat-value">{depot.stats.hazirlaniyor}</div>
                                            </div>

                                            <div className="stat-row">
                                                <div className="stat-label">Bayi Sevk </div>
                                                <div className="stat-value">{depot.dealer_dispatch.toplam}</div>
                                            </div>
                                        </div>

                                        <div className="total-section my-auto">
                                            <div className="total-circle">
                                                <div className="total-label">Toplam</div>
                                                <div className="total-value">{depot.toplam}</div>
                                            </div>
                                        </div>

                                        <div className="right-stats">
                                            <div className="stats-grid">
                                                {topRegions.map((region, index) => (
                                                    <div key={index} className="stat-box">
                                                        <div className="stat-box-label">{region.name}</div>
                                                        <div className="stat-box-value">{Math.max(0, region.value)}</div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Styles */}
                <style jsx>{`
                .page-container {
                    position: relative;
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                }
                
                .header-section {
                    background-color: white;
                    border-radius: 8px;
                    padding: 16px 20px;
                    box-shadow: none;
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                    border: 1px solid #e9ecef;
                    overflow: visible;
                    position: relative;
                }
                
                .header-content {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 16px;
                    width: 100%;
                    margin-bottom: 0;
                    flex-wrap: wrap;
                }
                
                .header-top-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    width: 100%;
                    margin-bottom: 12px;
                    order: 1;
                }
                
                .header-left-group {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    flex: 1;
                    min-width: 0;
                    order: 2;
                }
                
                .header-logo-container {
                    flex-shrink: 0;
                    width: 44px;
                    height: 44px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                
                .header-logo {
                    width: auto;
                    height: 65px;
                    object-fit: contain;
                    padding: 2px;
                }
                
                .brand-filter-container {
                    flex: 1;
                    min-width: 200px;
                    max-width: 300px;
                }

                .brand-warning-container {
                    position: absolute;
                    top: 100%;
                    left: 0;
                    right: 0;
                    z-index: 1000;
                }
                
                .brand-warning {
                    position: relative;
                    width: 100%;
                }
                
                .search-box-container {
                    flex: 1;
                    min-width: 250px;
                    max-width: 400px;
                    order: 3;
                }
                
                .action-buttons {
                    display: flex;
                    gap: 8px;
                    flex-shrink: 0;
                    align-items: center;
                    order: 4;
                }
                
                .dx-texteditor-buttons-container div{
                    background:transparent !important;
                }

                .dx-texteditor-buttons-container i{
                    color: #2B53C6 !important;
                    font-weight: 600 !important;
                }
                
                .search-box {
                    width: 100%;
                    position: relative;
                }

                .search-input-wrapper {
                    position: relative;
                    width: 100%;
                }

                .search-loading-indicator {
                    position: absolute;
                    right: 50px;
                    top: 50%;
                    transform: translateY(-50%);
                    color: #2B53C6;
                    font-size: 16px;
                    z-index: 10;
                    pointer-events: none;
                }
                
                .search-box :global(.dx-texteditor) {
                    background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
                    border: 2px solid #e9ecef;
                    border-radius: 12px;
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.02);
                    position: relative;
                    overflow: hidden;
                }

                .search-box :global(.dx-texteditor::before) {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: -100%;
                    width: 100%;
                    height: 100%;
                    background: linear-gradient(90deg, transparent, rgba(43, 83, 198, 0.1), transparent);
                    transition: left 0.5s;
                    z-index: 1;
                }
                
                .search-box :global(.dx-texteditor:hover) {
                    border-color: #2B53C6;
                    background: white;
                    box-shadow: 0 4px 12px rgba(43, 83, 198, 0.08);
                    transform: translateY(-1px);
                }

                .search-box :global(.dx-texteditor:hover::before) {
                    left: 100%;
                }
                
                .search-box :global(.dx-texteditor.dx-state-focused) {
                    border-color: #2B53C6;
                    background: white;
                    box-shadow: 0 0 0 4px rgba(43, 83, 198, 0.12), 0 4px 16px rgba(43, 83, 198, 0.1);
                    transform: translateY(-1px);
                }

                .search-box :global(.dx-texteditor-input) {
                    font-size: 15px;
                    font-weight: 500;
                    color: #1f2937;
                    letter-spacing: 0.3px;
                    padding: 0 16px;
                }

                .search-box :global(.dx-texteditor-input::placeholder) {
                    color: #9ca3af;
                    font-weight: 400;
                    font-style: italic;
                }

                .search-box :global(.dx-label) {
                    color: #6b7280;
                    font-weight: 600;
                    font-size: 13px;
                    letter-spacing: 0.5px;
                    text-transform: uppercase;
                }

                .search-box :global(.dx-texteditor.dx-state-focused .dx-label) {
                    color: #2B53C6;
                }
                
                .search-box :global(.dx-button.dx-button-default) {
                    background: linear-gradient(135deg, #2B53C6 0%, #1e40af 100%) !important;
                    border: none !important;
                    color: white !important;
                    border-radius: 8px !important;
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
                    box-shadow: 0 2px 8px rgba(43, 83, 198, 0.3) !important;
                    position: relative !important;
                    overflow: hidden !important;
                }

                .search-box :global(.dx-button.dx-button-default::before) {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: -100%;
                    width: 100%;
                    height: 100%;
                    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
                    transition: left 0.5s;
                }
                
                .search-box :global(.dx-button.dx-button-default:hover) {
                    background: linear-gradient(135deg, #1e40af 0%, #152B60 100%) !important;
                    transform: translateY(-1px) !important;
                    box-shadow: 0 4px 16px rgba(43, 83, 198, 0.4) !important;
                }

                .search-box :global(.dx-button.dx-button-default:hover::before) {
                    left: 100%;
                }

                .search-box :global(.dx-button.dx-button-default:active) {
                    transform: translateY(0) !important;
                    box-shadow: 0 2px 8px rgba(43, 83, 198, 0.3) !important;
                }

                .search-box :global(.dx-button.dx-button-default:disabled) {
                    background: #e5e7eb !important;
                    color: #9ca3af !important;
                    box-shadow: none !important;
                    transform: none !important;
                    cursor: not-allowed !important;
                }
                
                .search-box :global(.dx-button.dx-button-default .dx-icon) {
                    color: white !important;
                    font-size: 16px !important;
                    font-weight: 600 !important;
                }

                .search-box :global(.dx-button.dx-button-default:disabled .dx-icon) {
                    color: #9ca3af !important;
                }

                .search-box :global(.dx-clear-button-area) {
                    right: 50px !important;
                }

                .search-box :global(.dx-clear-button-area .dx-icon) {
                    color: #6b7280 !important;
                    transition: color 0.2s ease !important;
                }

                .search-box :global(.dx-clear-button-area:hover .dx-icon) {
                    color: #dc2626 !important;
                }

                .search-error-container {
                    position: absolute;
                    top: 100%;
                    left: 0;
                    right: 0;
                    z-index: 1000;
                }
                
                .search-error {
                    position: relative;
                    width: 100%;
                }

                @keyframes slideDown {
                    from {
                        opacity: 0;
                        transform: translateY(-10px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                @keyframes pulse {
                    0%, 100% {
                        opacity: 1;
                    }
                    50% {
                        opacity: 0.7;
                    }
                }

                .action-button {
                    height: 40px;
                    padding: 0 16px;
                    border: none;
                    border-radius: 8px;
                    font-size: 14px;
                    font-weight: 500;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    transition: all 0.2s ease;
                    cursor: pointer;
                    background: #f1f5f9;
                    color: #64748b;
                    white-space: nowrap;
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                    position: relative;
                    overflow: hidden;
                }
                
                .action-button:hover {
                    background: #e2e8f0;
                    color: #475569;
                    transform: translateY(-1px);
                }
                
                .action-button:active {
                    transform: translateY(0);
                    background: #cbd5e1;
                }
                
                .button-text {
                    display: inline;
                    font-weight: 500;
                }
                
                .fullscreen-button {
                    background: #f1f5f9;
                    color: #64748b;
                    padding: 0;
                    width: 40px;
                    min-width: 40px;
                    border: none;
                    font-size: 16px;
                }
                
                .fullscreen-button:hover {
                    background: #e2e8f0;
                    color: #2B53C6;
                    transform: translateY(-1px);
                }
                
                .pdf-button {
                    background: #f1f5f9;
                    color: #64748b;
                    min-width: 40px !important;
                    width: auto;
                    border: none;
                    font-size: 14px;
                }
                
                .pdf-button:hover {
                    background: #e2e8f0;
                    color: #2B53C6;
                    transform: translateY(-1px);
                }
                
                .pdf-button:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                    transform: none;
                    background: #f1f5f9;
                }
                
                .pdf-button:disabled:hover {
                    background: #f1f5f9;
                    color: #64748b;
                    transform: none;
                }
                
                .depot-card {
                    background-color: #ffffff;
                    border-radius: 6px;
                    overflow: hidden;
                    box-shadow: none;
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                    border: 1px solid #e9ecef;
                    box-shadow: 0 4px 12px rgba(38, 76, 189, 0.20);
                   
                }
                
                .depot-header {
                  //  background-color: rgb(239, 242, 247) !important;
                    padding: 15px 20px;
                    border-bottom: 1px solid #dee2e6a6;
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03);
                }
                
                .depot-header h2 {
                    margin: 0;
                    font-size: 1.6rem;
                    color: #2B53C6;
                   font-weight: 700;
                    color: #2B53C6;
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                }
                
                .depot-body {
                    padding: 20px;
                    display: grid;
                    grid-template-columns: 2fr 1fr 1fr;
                    grid-template-areas: "left-stats total-section right-stats";
                    gap: 20px;
                    align-items: start;
                }
                
                .left-stats {
                    grid-area: left-stats;
                    min-width: 0;
                    max-width: none;
                    height: 100%;
                    display: flex;
                    flex-direction: column;
                    justify-content: space-between;
                }
                
                .stat-row {
                    display: flex;
                    justify-content: space-between;
                    padding: 8px 0;
                    border-bottom: 1px solid #f0f0f0;
                }
                
                .stat-label {
                    font-size: 1rem;
                    color:rgb(30, 32, 34);
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                }
                
                .stat-value {
                    font-size: 1.1rem;
                    font-weight: 600;
                    color: #212529;
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                }
                
                .total-section {
                    grid-area: total-section;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 10px;
                    min-width: 200px;
                }
                
                 .total-circle {
                    background: linear-gradient(135deg, #2B53C6 0%, #1e40af 100%);
                    color: white;
                    width: 165px;
                    height: 165px;
                    border-radius: 50%;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    box-shadow: 0 4px 12px rgba(43, 83, 198, 0.3);
                    transition: all 0.3s ease;
                    margin:auto;
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                }
                 .total-circle:hover {
                    transform: scale(1.05);
                }
                
                .total-label {
                    font-size: 1.2rem;
                    opacity: 0.9;
                    margin-bottom: 4px;
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                }
                
                .total-value {
                    font-size: 2.2rem;
                    font-weight: 500;
                    line-height: 1;
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                }
                
                .right-stats {
                    grid-area: right-stats;
                    display: flex;
                    justify-content: flex-end;
                    min-width: 0;
                    max-width: none;
                }
                
                .stats-grid {
                    display: grid;
                    grid-template-columns: repeat(2, minmax(140px, 1fr));
                    gap: 15px;
                    width: 100%;
                    max-width: 400px;
                }
                
                .stat-box {
                    background-color:rgb(239, 242, 247);
                    border: 1px solid #e9ecef;
                    border-radius: 8px;
                    padding: 20px 15px;
                    text-align: center;
                    transition: all 0.2s ease;
                    min-height: 100px;
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    align-items: center;
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
                }
                
                .stat-box:hover {
                    transform: translateY(-1px);
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
                    border-color: #dee2e6;
                }
                
                .stat-box-label {
                    font-size: 1.1rem;
                    color: #6c757d;
                    margin-bottom: 8px;
                    font-weight: 500;
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                }
                
                .stat-box-value {
                    font-size: 1.8rem;
                    font-weight: 700;
                    color: #212529;
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                    line-height: 1.2;
                }
                
                /* Updated Modal Styles */
                :global(.modal-backdrop) {
                    z-index: 999998 !important;
                }

                :global(.chassis-modal) {
                    z-index: 999999 !important;
                }

                .chassis-modal-header {
                    background: linear-gradient(to top, #152B60 0%, #2B53C6 100%);
                    color: white;
                    padding: 15px 20px;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    border-top-left-radius: 12px;
                    border-top-right-radius: 12px;
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                }

                .uk-header {
                    display: flex;
                    flex-direction: column;
                }

                .uk-title {
                    font-size: 1.5rem;
                    font-weight: bold;
                    margin-bottom: 4px;
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                }

                .uk-email {
                    font-size: 1rem;
                    opacity: 0.9;
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                }

                .close-btn {
                    background: none;
                    border: none;
                    color: white;
                    font-size: 2rem;
                    line-height: 1;
                    cursor: pointer;
                    padding: 0;
                    opacity: 0.8;
                    transition: opacity 0.2s;
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                }

                .close-btn:hover {
                    opacity: 1;
                }

                .modal-body {
                    overflow-y: auto;
                    max-height: calc(90vh - 80px);
                    width: 100%;
                    padding: 0;
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                }

                .chassis-info-container {
                    padding: 16px;
                    width: 100%;
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                }

                .info-row {
                    display: flex;
                    flex-direction: column;
                    padding: 12px 16px;
                    width: 100%;
                    border-bottom: 1px solid #dee2e6;
                }

                .info-row:last-child {
                    border-bottom: none;
                }

                .info-col {
                    display: flex;
                    align-items: flex-start;
                    width: 100%;
                    padding: 4px 0;
                }

                .info-label {
                    color: #6c757d;
                    font-size: 0.875rem;
                    width: 140px;
                    flex-shrink: 0;
                    padding-right: 12px;
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                }

                .info-value {
                    font-size: 0.875rem;
                    font-weight: 600;
                    color: #212529;
                    flex: 1;
                    word-break: break-word;
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                }

                .sase-value {
                    font-size: 1rem;
                    font-weight: 700;
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                }

                /* Desktop layout for modal */
                @media (min-width: 768px) {
                    .modal-box {
                        max-width: 900px;
                        width: 85%;
                    }

                    .chassis-info-container {
                        padding: 32px;
                    }

                    .info-row {
                        display: flex;
                        flex-direction: row;
                        align-items: center;
                        padding: 15px 0;
                        width: 100%;
                        border-bottom: 1px solid #dee2e6;
                        min-height: 70px;
                    }

                    .info-row:first-child {
                        border-bottom: 2px solid #dee2e6;
                        margin-bottom: 12px;
                        padding-bottom: 24px;
                    }

                    .info-col {
                        display: flex;
                        flex-direction: column;
                        flex: 1;
                        min-width: 0;
                        padding: 0 16px;
                    }

                    .info-col:first-child {
                        padding-left: 0;
                    }

                    .info-col:last-child {
                        padding-right: 0;
                    }

                    .info-label {
                        color: #6c757d;
                        font-size: 0.9rem;
                        font-weight: 500;
                        margin-bottom: 8px;
                        text-transform: uppercase;
                        letter-spacing: 0.5px;
                        width: auto;
                        padding-right: 0;
                    }

                    .info-value {
                        font-size: 1.1rem;
                        font-weight: 600;
                        color: #212529;
                        word-break: break-word;
                        line-height: 1.4;
                    }

                    .sase-value {
                        font-size: 1.25rem;
                        font-weight: 700;
                        color: #1e40af;
                        letter-spacing: 0.5px;
                    }
                }

                /* Medium screens (481px-767px) - tablet portrait */
                @media (min-width: 481px) and (max-width: 767px) {
                    .header-section {
                        padding: 14px 16px;
                    }

                    .header-content {
                        flex-direction: column;
                        gap: 12px;
                        align-items: stretch;
                    }

                    .header-top-row {
                        width: 100%;
                        margin-bottom: 8px;
                        order: 1;
                    }

                    .header-left-group {
                        flex-direction: row;
                        order: 2;
                        width: 100%;
                    }

                    .brand-filter-container {
                        flex: 1;
                        min-width: 180px;
                        max-width: none;
                    }

                    .search-box-container {
                        order: 3;
                        width: 100%;
                        max-width: none;
                    }

                    .pdf-button .button-text {
                        display: inline;
                    }
                }

                /* Mobile (max 480px) */
                @media (max-width: 480px) {
                    .header-section {
                        padding: 12px;
                    }

                    .header-content {
                        flex-direction: column;
                        gap: 10px;
                        align-items: stretch;
                    }

                    .header-top-row {
                        margin-bottom: 6px;
                    }

                    .header-left-group {
                        flex-direction: column;
                        gap: 8px;
                        order: 2;
                    }

                    .header-logo-container {
                        align-self: flex-start;
                    }

                    .brand-filter-container {
                        width: 100%;
                        max-width: none;
                    }

                    .search-box-container {
                        order: 3;
                        width: 100%;
                    }

                    .action-button {
                        height: 36px;
                        font-size: 13px;
                        padding: 0 12px;
                    }

                    .fullscreen-button {
                        width: 36px;
                        min-width: 36px;
                        padding: 0;
                    }

                    .pdf-button .button-text {
                        display: none;
                    }

                    .pdf-button {
                        width: auto;
                        min-width: auto;
                        padding: 0 8px;
                    }
                }

                /* Desktop (1200px+) */
                @media (min-width: 1200px) {
                    .header-content {
                        flex-wrap: nowrap;
                        gap: 20px;
                    }
                    
                    .header-top-row {
                        display: contents;
                    }
                    
                    .header-left-group {
                        order: 1;
                        flex: 0 0 auto;
                        width: auto;
                    }
                    
                    .brand-filter-container {
                        max-width: 280px;
                    }
                    
                    .search-box-container {
                        order: 2;
                        flex: 1;
                        margin: 0 auto;
                        max-width: 400px;
                    }
                    
                    .action-buttons {
                        order: 3;
                    }
                }

                /* Medium screens: left-stats on top, total-section and right-stats below side by side */
                @media (max-width: 1200px) {
                    .depot-body {
                        grid-template-columns: 1fr 1fr;
                        grid-template-areas: 
                            "left-stats left-stats"
                            "total-section right-stats";
                        gap: 20px;
                    }
                    
                    .total-section {
                        justify-content: flex-start;
                    }
                    
                    .right-stats {
                        justify-content: flex-end;
                    }
                }
                
                /* Small screens: all three stacked vertically */
                @media (max-width: 768px) {
                    .depot-body {
                        grid-template-columns: 1fr;
                        grid-template-areas: 
                            "left-stats"
                            "total-section"
                            "right-stats";
                        gap: 20px;
                    }
                    
                    .total-section {
                        justify-content: center;
                    }
                    
                    .right-stats {
                        justify-content: center;
                    }
                    
                    .stats-grid {
                        max-width: none;
                        justify-content: center;
                    }
                }

                /* Scroll to top button - only visible on mobile */
                .scroll-to-top-button {
                    position: fixed;
                    bottom: 20px;
                    right: 20px;
                    width: 42px;
                    height: 42px;
                    background: rgba(43, 83, 198, 0.7);
                    color: white;
                    border: none;
                    border-radius: 50%;
                    display: none;
                    align-items: center;
                    justify-content: center;
                    font-size: 18px;
                    cursor: pointer;
                    z-index: 1000;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
                    transition: all 0.3s ease;
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                    opacity: 0.8;
                    backdrop-filter: blur(10px);
                    font-weight: 600;
                }

                .scroll-to-top-button:hover {
                    background: rgba(21, 43, 96, 0.9);
                    opacity: 1;
                    transform: translateY(-1px);
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
                }

                .scroll-to-top-button:active {
                    transform: translateY(0);
                    opacity: 0.9;
                }

                /* Show scroll to top button only on mobile screens */
                @media (max-width: 768px) {
                    .scroll-to-top-button {
                        display: flex;
                    }
                }

                /* Adjust position for very small screens */
                @media (max-width: 480px) {
                    .scroll-to-top-button {
                        bottom: 15px;
                        right: 15px;
                        width: 38px;
                        height: 38px;
                        font-size: 16px;
                    }
                }

                .search-box :global(.dx-button.searching) {
                    animation: pulse 1.5s infinite !important;
                }

                .search-box :global(.dx-button.searching .dx-icon) {
                    animation: spin 1s linear infinite !important;
                }

                @keyframes spin {
                    from {
                        transform: rotate(0deg);
                    }
                    to {
                        transform: rotate(360deg);
                    }
                }

                .live-circle-indicator {
                    position: fixed;
                    bottom: 20px;
                    right: 20px;
                    width: 32px;
                    height: 32px;
                    background-color: white;
                    border-radius: 50%;
                    border: 2px solid #e9ecef;
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 9999;
                }

                .live-circle-indicator::before {
                    content: '';
                    width: 14px;
                    height: 14px;
                    background-color: #22c55e;
                    border-radius: 50%;
                    animation: pulse 2s infinite;
                    box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.7);
                }

                .live-circle-indicator.waiting::before {
                    background-color:rgb(249, 158, 22);
                    animation: pulseOrange 2s infinite;
                    box-shadow: 0 0 0 0 rgba(249, 143, 22, 0.7);
                }

                .live-circle-indicator.error::before {
                    background-color: #dc2626;
                    animation: pulseRed 2s infinite;
                    box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.7);
                }

                @keyframes pulse {
                    0% {
                        transform: scale(0.95);
                        box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.7);
                    }
                    
                    70% {
                        transform: scale(1);
                        box-shadow: 0 0 0 10px rgba(34, 197, 94, 0);
                    }
                    
                    100% {
                        transform: scale(0.95);
                        box-shadow: 0 0 0 0 rgba(34, 197, 94, 0);
                    }
                }

                @keyframes pulseOrange {
                    0% {
                        transform: scale(0.95);
                        box-shadow: 0 0 0 0 rgba(249, 115, 22, 0.7);
                    }
                    
                    70% {
                        transform: scale(1);
                        box-shadow: 0 0 0 10px rgba(249, 115, 22, 0);
                    }
                    
                    100% {
                        transform: scale(0.95);
                        box-shadow: 0 0 0 0 rgba(249, 115, 22, 0);
                    }
                }

                @keyframes pulseRed {
                    0% {
                        transform: scale(0.95);
                        box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.7);
                    }
                    
                    70% {
                        transform: scale(1);
                        box-shadow: 0 0 0 10px rgba(220, 38, 38, 0);
                    }
                    
                    100% {
                        transform: scale(0.95);
                        box-shadow: 0 0 0 0 rgba(220, 38, 38, 0);
                    }
                }

                @media (max-width: 768px) {
                    .live-circle-indicator {
                        position: fixed;
                        bottom: 5px !important;
                        left: 5px;
                        top: auto;
                        right: auto;
                        width: 22px;
                        height: 22px;
                        display: flex !important;
                        z-index: 9999;
                    }

                    .live-circle-indicator::before {
                        content: '';
                        width: 10px !important;
                        height: 10px !important;
                        display: block !important;
                        background-color: #22c55e !important;
                    }

                    .live-circle-indicator.waiting::before {
                        background-color: #f97316 !important;
                    }

                    .live-circle-indicator.error::before {
                        background-color: #dc2626 !important;
                    }
                }

                /* Loading styles */
                .loading-container {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    min-height: 200px;
                    width: 100%;
                }

                .loading-indicator {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 16px;
                }

                .loading-spinner {
                    width: 40px;
                    height: 40px;
                    border: 3px solid #f3f3f3;
                    border-top: 3px solid #2B53C6;
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                }

                .loading-text {
                    color: #2B53C6;
                    font-size: 14px;
                    font-weight: 500;
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                    letter-spacing: 0.5px;
                }

                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }

                /* Loading and fade-in styles */
                .loading-container {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    min-height: 200px;
                    width: 100%;
                }

                .loading-indicator {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 16px;
                }

                .loading-spinner {
                    width: 40px;
                    height: 40px;
                    border: 3px solid #f3f3f3;
                    border-top: 3px solid #2B53C6;
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                }

                .loading-text {
                    color: #2B53C6;
                    font-size: 14px;
                    font-weight: 500;
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                    letter-spacing: 0.5px;
                }

                .depots-container {
                    opacity: 0;
                    animation: fadeIn 0.5s ease-out forwards;
                }

                .depot-card {
                    opacity: 0;
                    transform: translateY(20px);
                    animation: fadeInUp 0.3s ease-out forwards;
                }

                .depot-card:nth-child(1) { animation-delay: 0.1s; }
                .depot-card:nth-child(2) { animation-delay: 0.2s; }
                .depot-card:nth-child(3) { animation-delay: 0.3s; }
                .depot-card:nth-child(4) { animation-delay: 0.4s; }
                .depot-card:nth-child(5) { animation-delay: 0.5s; }

                @keyframes fadeIn {
                    from {
                        opacity: 0;
                    }
                    to {
                        opacity: 1;
                    }
                }

                @keyframes fadeInUp {
                    from {
                        opacity: 0;
                        transform: translateY(20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
            `}</style>
            </div>
            {showScrollTop && (
                <Draggable handle='.scroll-to-top-button'
                    allowAnyClick={false}
                    onStart={handleDragStart}
                    onDrag={handleDrag}
                    onStop={handleDragStop}
                >
                    <button
                        className="scroll-to-top-button"
                    >
                        <i className="fa fa-arrow-up"></i>
                    </button>
                </Draggable>
            )}
        </>
    );
};

export default YoneticiEkranlari;


